import { TFunction } from 'i18next';
import { NewService, Service } from '../../../models/Entities/Service';
import { ServiceCategory } from '../../../models/Entities/ServiceCategory';
import { AppState } from '../../../redux/reducers';
import * as ServiceStore from '../../../redux/middleware/ServiceMiddleware';
import * as ConsumerStore from '../../../redux/middleware/ConsumerMiddleware';
import * as ServiceCategoryStore from '../../../redux/middleware/ServiceCategoryMiddleware';
import * as MedicalProtocolStore from '../../../redux/middleware/MedicalProtocolMiddleware';
import * as ContractStore from '../../../redux/middleware/ContractMiddleware';

export interface ServiceFormProps {
  isOpen: boolean;
  openPanel: () => void;
  dismissPanel: () => void;
  Service?: Service;
}

interface ConnectedProps {
  ServiceState: AppState['Services'];
  ConsumerState: AppState['Consumers'];
  ServiceCategoryState: AppState['ServiceCategories'];
  MedicalProtocolsState: AppState['MedicalProtocols'];
  ContractState: AppState['Contract'];
  isLoadingAssociatedContracts?: boolean;
}

export interface ServiceDispatchProps {
  AddService: (...args: Parameters<typeof ServiceStore.actionCreators.AddService>) => void;
  UpdateService: (...args: Parameters<typeof ServiceStore.actionCreators.UpdateService>) => void;
  GetAllConsumers: (...args: Parameters<typeof ConsumerStore.actionCreators.GetAllConsumers>) => void;
  GetAllServiceCategories: (...args: Parameters<typeof ServiceCategoryStore.actionCreators.GetAllServiceCategories>) => void;
  GetProtocolPatients: (...args: Parameters<typeof MedicalProtocolStore.actionCreators.GetProtocolPatients>) => void;
  GetAllProtocolContractAssociations: (
    ...args: Parameters<typeof MedicalProtocolStore.actionCreators.GetAllProtocolContractAssociations>
  ) => void;
  GetContractById: (...args: Parameters<typeof ContractStore.actionCreators.GetContractById>) => void;
}

export type Props = ServiceFormProps & ConnectedProps & ServiceDispatchProps;

export const mapStateToProps = (state: AppState) => ({
  ServiceState: state.Services,
  ConsumerState: state.Consumers,
  ServiceCategoryState: state.ServiceCategories,
  MedicalProtocolsState: state.MedicalProtocols,
  ContractState: state.Contract,
  isLoadingAssociatedContracts: state.MedicalProtocols?.isGettingProtocolContractAssociation
});

export const mapDispatchToProps: ServiceDispatchProps = {
  AddService: ServiceStore.actionCreators.AddService,
  UpdateService: ServiceStore.actionCreators.UpdateService,
  GetAllConsumers: ConsumerStore.actionCreators.GetAllConsumers,
  GetAllServiceCategories: ServiceCategoryStore.actionCreators.GetAllServiceCategories,
  GetProtocolPatients: MedicalProtocolStore.actionCreators.GetProtocolPatients,
  GetAllProtocolContractAssociations: MedicalProtocolStore.actionCreators.GetAllProtocolContractAssociations,
  GetContractById: ContractStore.actionCreators.GetContractById
};

export interface FormValidation {
  success: boolean;
  errors: string[];
}

export interface ValidateFormInput {
  description?: string;
  startDate?: Date;
  endDate?: Date;
  estimateCost: string;
  finalCost: string;
}

export interface ServicePayloadInput {
  protocolId?: number;
  stageId?: number;
  numberInProtocol: string[];
  consumerId: string;
  providerId?: string;
  serviceCategoryId: ServiceCategory[];
  description?: string;
  serviceStatusId: number;
  startDate?: Date;
  endDate?: Date;
  originPlace?: string;
  destinationPlace?: string;
  estimateCost: string;
  finalCost: string;
  currencyType?: string;
  attachReceipt: boolean;
  receivesPerDiem: boolean;
  takesBreakfast: boolean;
}

export const validateForm = (
  input: ValidateFormInput,
  t: TFunction
): FormValidation => {
  const errors: string[] = [];

  if (!input.description) {
    errors.push(t('serviceForm.formValidation.requiredDescription'));
  }

  if (!input.startDate) {
    errors.push(t('serviceForm.formValidation.requiredStartDate'));
  }

  if (!input.estimateCost || Number.parseFloat(input.estimateCost) < 0) {
    errors.push(t('serviceForm.formValidation.requiredEstimateCost'));
  }

  if (!input.finalCost || Number.parseFloat(input.finalCost) < 0) {
    errors.push(t('serviceForm.formValidation.requiredFinalCost'));
  }

  if (input.endDate && input.startDate && input.endDate < input.startDate) {
    errors.push(t('serviceForm.formValidation.invalidDate'));
  }

  return {
    success: errors.length === 0,
    errors
  };
};

export const buildExistingServicePayload = (
  input: ServicePayloadInput & { service: Service }
): Service => ({
  id: input.service.id,
  parentId: null,
  protocolId: input.protocolId,
  stageId: input.stageId,
  numberInProtocol:
    input.numberInProtocol && input.numberInProtocol.length > 0
      ? input.numberInProtocol[0]
      : undefined,
  consumerId: input.consumerId,
  providerId: input.providerId || null,
  serviceCategoryId:
    input.serviceCategoryId && input.serviceCategoryId.length > 0
      ? input.serviceCategoryId[0].id
      : null,
  description: input.description || null,
  serviceStatusId: input.serviceStatusId,
  startDate: input.startDate!,
  endDate: input.endDate || null,
  originPlace: input.originPlace || null,
  destinationPlace: input.destinationPlace || null,
  estimateCost: Number.parseFloat(input.estimateCost),
  finalCost: Number.parseFloat(input.finalCost),
  currencyType: input.currencyType || null,
  attachReceipt: input.attachReceipt,
  receivesPerDiem: input.receivesPerDiem,
  takesBreakfast: input.takesBreakfast,
  created: input.service.created,
  createdBy: input.service.createdBy,
  lastModified: new Date(),
  lastModifiedBy: null
});

export const buildNewServicePayload = (
  input: ServicePayloadInput
): NewService => ({
  parentId: null,
  protocolId: input.protocolId,
  stageId: input.stageId,
  numberInProtocol:
    input.numberInProtocol && input.numberInProtocol.length > 0
      ? input.numberInProtocol[0]
      : undefined,
  consumerId: input.consumerId,
  providerId: input.providerId || null,
  serviceCategoryId:
    input.serviceCategoryId && input.serviceCategoryId.length > 0
      ? input.serviceCategoryId[0].id
      : null,
  description: input.description || null,
  serviceStatusId: input.serviceStatusId,
  startDate: input.startDate!,
  endDate: input.endDate || null,
  originPlace: input.originPlace || null,
  destinationPlace: input.destinationPlace || null,
  estimateCost: Number.parseFloat(input.estimateCost),
  finalCost: Number.parseFloat(input.finalCost),
  currencyType: input.currencyType || null,
  attachReceipt: input.attachReceipt,
  receivesPerDiem: input.receivesPerDiem,
  takesBreakfast: input.takesBreakfast
});
